import { useQueries } from '@tanstack/react-query';
import { CommentsApi, type GroupedCommentsDto } from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { useAuthState } from '@/hooks/auth/useAuth';
import { useTaxBinders } from '@/hooks/api/useTaxBinders';
import { useClient } from '@/hooks/context/useClient';
import { groupedCommentKeys } from '@/hooks/api/useGroupedComments';

interface PerBinderUnread {
  binderId: string;
  binderName: string;
  unreadCount: number;
}

interface UnreadMessageCountResult {
  /** Total unread messages across all binders */
  totalUnread: number;
  /** Per-binder unread counts (only binders with unread > 0) */
  perBinder: Map<string, PerBinderUnread>;
  /**
   * ID of the binder with unread messages when it is the ONLY one — used by the
   * Messages nav item to deep-link straight to it. Null when zero or multiple
   * binders have unread (the nav then goes to the binders list instead).
   */
  soleUnreadBinderId: string | null;
  /** Whether any of the queries are still loading */
  isLoading: boolean;
}

/**
 * Hook to aggregate unread message counts across all client binders.
 *
 * Fetches grouped comments for each binder the client has access to,
 * then sums the unreadComments field from each GroupedCommentsDto.
 *
 * Since clients typically have 1-5 binders, the per-binder fetch is acceptable.
 */
export function useUnreadMessageCount(): UnreadMessageCountResult {
  const { data: authState } = useAuthState();
  const { selectedClient } = useClient();
  const token = authState?.token || undefined;
  const isAuthenticated = !!authState?.isAuthenticated && !!token;
  const hasClient = !!selectedClient?.id;

  // Fetch the client's binder list — only when a client is selected to avoid
  // firing requests without the X-Active-Client-Id header (which returns 400)
  const { data: bindersResponse, isLoading: isBindersLoading } = useTaxBinders(
    {},
    { activeClientId: selectedClient?.id, enabled: isAuthenticated && hasClient }
  );

  // Only process binders when authenticated AND a client is selected
  const binders = isAuthenticated && hasClient ? bindersResponse?.data?.items || [] : [];

  // For each binder, fetch grouped comments to get unread counts
  const commentQueries = useQueries({
    queries: binders.map((binder) => ({
      queryKey: groupedCommentKeys.filtered({
        binderId: binder.id || '',
        includeResolved: true,
        sortBy: 'latestComment' as const,
      }),
      queryFn: async (): Promise<GroupedCommentsDto> => {
        const commentsApi = new CommentsApi(createApiConfig(token), undefined, apiAxiosInstance);
        const response = await commentsApi.apiCommentsGroupedGet({
          binderId: binder.id || '',
          includeResolved: true,
          sortBy: 'latestComment',
        });
        // response.data is GroupedCommentsDtoApiResponse
        // response.data.data is the actual GroupedCommentsDto
        return response.data.data!;
      },
      enabled: isAuthenticated && hasClient && !!binder.id,
      staleTime: 1000 * 60 * 2, // 2 minutes — balance freshness with request volume
    })),
  });

  const isLoading = isBindersLoading || commentQueries.some((q) => q.isLoading);

  let totalUnread = 0;
  const perBinder = new Map<string, PerBinderUnread>();

  for (let i = 0; i < binders.length; i++) {
    const binder = binders[i];
    const query = commentQueries[i];
    const unreadCount = query?.data?.unreadComments ?? 0;

    if (unreadCount > 0 && binder.id) {
      totalUnread += unreadCount;
      perBinder.set(binder.id, {
        binderId: binder.id,
        binderName: binder.name || 'Untitled Binder',
        unreadCount,
      });
    }
  }

  // Only deep-link from the nav when exactly one binder has unread messages AND
  // every binder's unread query has settled successfully. useQueries entries resolve
  // independently, so mid-load (or when a query has failed) perBinder.size can
  // transiently equal 1 while other binders are still unknown — deep-linking then
  // would misdirect the user. Until all queries have settled, fall back to the list.
  const allUnreadQueriesSettled = !isLoading && !commentQueries.some((q) => q.isError);
  const soleUnreadBinderId =
    allUnreadQueriesSettled && perBinder.size === 1 ? perBinder.values().next().value!.binderId : null;

  return { totalUnread, perBinder, soleUnreadBinderId, isLoading };
}
